import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/errors';
import {
  ProductSkuResponse,
  CreateProductSkuRequest,
  UpdateProductSkuRequest,
  CreateBulkProductSkuRequest,
  CreateBulkProductSkuResponse,
  UpdateProductSkuResponse,
  CreateProductSkuResponse,
  ListProductSkusResponse,
  ListProductSkusFilterRequest,
  ProductSkuWithInventoryResponse,
  ProductSkuWithInventoryResponsePagedResults
} from '../models';
import { environment } from 'apps/ff-frontend/src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProductSkuService {
  private readonly API_BASE_URL =  environment.RETAIL_BILLING_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {}

  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Company-level SKU operations
  getCompanyProductSkus(
    productId: number, requestParams:any
  ): Observable<ListProductSkusResponse> {
   
    
    let parsedParams = new HttpParams()
    requestParams.forEach((p: any) => {
      parsedParams = parsedParams.append(p.name, p.value);
    });
    
    return this.http.get<ListProductSkusResponse>(`${this.API_BASE_URL}/companies/retail_products/${productId}/skus`,  {
      headers: this.httpOptions.headers,
      params: parsedParams
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Product SKUs API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getCompanyProductSku(productId: number, skuId: number): Observable<ProductSkuResponse> {

    return this.http.get<ProductSkuResponse>(`${this.API_BASE_URL}/companies/retail_products/${productId}/skus/${skuId}`, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createCompanyProductSkus(productId: number, request: CreateBulkProductSkuRequest): Observable<CreateBulkProductSkuResponse> {
    return this.http.post<CreateBulkProductSkuResponse>(`${this.API_BASE_URL}/companies/retail_products/${productId}/skus`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Company Product SKUs API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCompanyProductSku(productId: number, skuId: number, request: UpdateProductSkuRequest): Observable<UpdateProductSkuResponse> {
    return this.http.put<UpdateProductSkuResponse>(`${this.API_BASE_URL}/companies/retail_products/${productId}/skus/${skuId}`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Company Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Tenant-level SKU operations
  getTenantProductSkus(
    tenantId: number,
    productId: number, 
    requestParams:any
  ): Observable<ListProductSkusResponse> {
    let parsedParams = new HttpParams()
    requestParams.forEach((p: any) => {
      parsedParams = parsedParams.append(p.name, p.value);
    });
    
    return this.http.get<ListProductSkusResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}/skus`, {
      headers: this.httpOptions.headers,
      params: parsedParams
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant Product SKUs API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantProductSku(tenantId: number, productId: number, skuId: number): Observable<ProductSkuResponse> {
    return this.http.get<ProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}/skus/${skuId}`, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createTenantProductSkus(tenantId: number, productId: number, request: CreateBulkProductSkuRequest): Observable<CreateBulkProductSkuResponse> {
    return this.http.post<CreateBulkProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}/skus`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Tenant Product SKUs API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateTenantProductSku(tenantId: number, productId: number, skuId: number, request: UpdateProductSkuRequest): Observable<UpdateProductSkuResponse> {
    return this.http.put<UpdateProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/${productId}/skus/${skuId}`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Tenant Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // SKU with inventory operations
  getTenantSkuWithInventory(tenantId: number, skuId: number): Observable<ProductSkuWithInventoryResponse> {
    return this.http.get<ProductSkuWithInventoryResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/skus/${skuId}`, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant SKU with Inventory API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantSkusWithInventory(
    tenantId: number,
    pageNumber?: number,
    pageSize?: number,
    includeOutOfStock: boolean = true
  ): Observable<ProductSkuWithInventoryResponsePagedResults> {
    const params: any = { include_out_of_stock: includeOutOfStock };
    if (pageNumber !== undefined) params.PageNumber = pageNumber;
    if (pageSize !== undefined) params.PageSize = pageSize;
    
    return this.http.get<ProductSkuWithInventoryResponsePagedResults>(`${this.API_BASE_URL}/tenants/${tenantId}/retail_products/skus`, {
      headers: this.httpOptions.headers,
      params: this.buildParams(params)
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Tenant SKUs with Inventory API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Legacy SKU operations for backward compatibility
  getProductSkus(
    tenantId: number,
    productId: number,
    request?: any
  ): Observable<ListProductSkusResponse> {
    return this.http.get<ListProductSkusResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/products/${productId}/skus`, {
      headers: this.httpOptions.headers,
      params: request ? this.buildParams(request) : undefined
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Product SKUs API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getProductSku(tenantId: number, productId: number, skuId: number): Observable<ProductSkuResponse> {
    return this.http.get<ProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/products/${productId}/skus/${skuId}`, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createProductSku(tenantId: number, productId: number, request: CreateProductSkuRequest): Observable<CreateProductSkuResponse> {
    return this.http.post<CreateProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/products/${productId}/skus`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateProductSku(tenantId: number, productId: number, skuId: number, request: UpdateProductSkuRequest): Observable<UpdateProductSkuResponse> {
    return this.http.put<UpdateProductSkuResponse>(`${this.API_BASE_URL}/tenants/${tenantId}/products/${productId}/skus/${skuId}`, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Product SKU API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}